import { useTranslate } from '@tolgee/react';
import React, { FC, useState, useEffect, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import CreateFolderModal from '../modals/folderModal';
import '../index.css';
import DeleteConfirmation from '../../../cards/itemDeleteConfirmation';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import { moveTextCaptionToFolder } from '../../../../../redux/slices/textBlocks';
import { lomavisShowToastr } from '../../../../../utils/ui/toastr';

interface folderCardProps {
  folderName: string;
  active: boolean;
  showFolderAdd: boolean;
  loading: boolean;
  folderUuid?: string; // Add folderUuid property
  handleCardClick: () => void;
  addSubfolder: (data: {
    name: string;
    editUuid?: string;
    folderUuid?: string;
  }) => void;
  editFolder: (data: {
    name: string;
    editUuid?: string;
    folderUuid?: string;
  }) => void;
  deleteFolder: () => void;
}

const FolderCard: FC<folderCardProps> = ({
  folderName,
  active,
  showFolderAdd,
  loading,
  folderUuid,
  handleCardClick,
  editFolder,
  deleteFolder,
  addSubfolder
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setCreateShowModal] = useState(false);
  const [showEditModal, setEditShowModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const [showDeleteConf, setShowDeleteConf] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { cloudUuid, locationUuid } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  // Handle drag over event
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    // Get the text block UUID from the drop event
    const textBlockUuid = e.dataTransfer.getData('text/plain');

    if (textBlockUuid) {
      // Dispatch the action to move the text block to this folder
      dispatch(
        moveTextCaptionToFolder(
          locationUuid,
          cloudUuid,
          textBlockUuid,
          folderUuid, // Use the folderUuid property
          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.success_captionMoved'),
              'success'
            );
          },
          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.err_captionMoved'),
              'error'
            );
          }
        )
      );
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div
      className={`d-flex align-items-center justify-content-between w-100 folder-row rounded-1 px-1 cursor-pointer ${isDragOver ? 'bg-light border border-primary' : ''}`}
      tabIndex={0}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        onClick={handleCardClick}
        className="d-flex align-items-center gap-2 cursor-pointer w-100"
      >
        <div
          className="position-relative d-flex align-items-center justify-content-center me-2"
          style={{ aspectRatio: '1' }} // `aspectRatio` keeps it square
        >
          <i className="fa-duotone fa-folder fs-2 w-100 mx-auto "></i>

          {loading && (
            <div
              className="position-absolute d-flex align-items-center justify-content-center m-auto"
              style={{
                inset: 0
              }}
            >
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              />
            </div>
          )}
        </div>
        <Button
          variant="light"
          size="sm"
          style={{ background: 'none', border: 'none', boxShadow: 'none' }}
          className={` ${
            active ? 'text-primary' : 'text-gray-700'
          } p-0 m-0 fs-6 px-1 w-100 text-start fw-bold `}
        >
          {folderName}
        </Button>
      </div>
      <div
        className="d-flex align-items-center gap-3 position-relative folder-actions"
        ref={dropdownRef}
      >
        <Dropdown show={showDropdown} onToggle={() => {}} align="end">
          <Dropdown.Toggle
            as="span"
            onClick={() => setShowDropdown((prev) => !prev)}
            className="cursor-pointer no-caret"
            style={{ border: 'none', background: 'none' }}
          >
            <i className="fa-duotone fa-solid fa-ellipsis fs-2"></i>
          </Dropdown.Toggle>

          <Dropdown.Menu onClick={() => setShowDropdown(false)}>
            <Dropdown.Item onClick={() => setEditShowModal(true)}>
              {t('lomavisCreator.textBlock.editFolder')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                setShowDeleteConf(true);
              }}
              className="text-danger"
            >
              {t('lomavisCreator.textBlock.deleteFolder')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        {showFolderAdd && (
          <i
            onClick={() => setCreateShowModal(true)}
            className="fa-solid fa-plus cursor-pointer fs-3"
          ></i>
        )}
      </div>
      {showCreateModal && (
        <CreateFolderModal
          show={showCreateModal}
          onClose={() => setCreateShowModal(false)}
          onCreate={addSubfolder}
          action="create"
        />
      )}
      {showEditModal && (
        <CreateFolderModal
          show={showEditModal}
          onClose={() => setEditShowModal(false)}
          onCreate={editFolder}
          name={folderName}
          action="edit"
        />
      )}

      <DeleteConfirmation
        handleCancel={() => setShowDeleteConf(false)}
        handleDelete={() => {
          deleteFolder();
          setShowDeleteConf(false);
        }}
        keyText={'folder_delete'}
        show={showDeleteConf}
      />
    </div>
  );
};

export default FolderCard;
